import { ISearchRequestObject } from '@logz-build/typescript';
import { SavedSearchDashboardUsage, UnifiedDashboardModel } from '@logz-pkg/models';
import { GlobalDatasourceResource, DatasourceSpec, ProjectMetadata } from '@perses-dev/core';
import { CrudApiService } from '../utilities/crud.api.service';
import { ICrudApiService } from '../utilities/types';

const buildParams = (params?: Record<string, string>) => {
  if (!params || !Object.entries(params).length) return '';

  return `?${new URLSearchParams(params)}`;
};

class DatasourceResponse implements GlobalDatasourceResource {
  kind: 'GlobalDatasource';
  metadata: ProjectMetadata;
  spec: DatasourceSpec;
}

export class DashboardApiService implements ICrudApiService<UnifiedDashboardModel> {
  crudApiService = new CrudApiService({ modelDefinition: UnifiedDashboardModel });
  datasourceApiService = new CrudApiService({ modelDefinition: DatasourceResponse });
  savedSearchUsagesApiService = new CrudApiService();

  urls = {
    project: `/perses/api/v1/dashboards`,
    byId: (uid: string) => `/perses/api/v1/dashboards/${encodeURIComponent(uid)}`,
    resource: folderId => `/perses/api/v1/projects/${encodeURIComponent(folderId)}/dashboards`,
    instance: (uid: string, folderId: string, params?: Record<string, string>) =>
      `/perses/api/v1/projects/${encodeURIComponent(folderId)}/dashboards/${encodeURIComponent(uid)}${buildParams(
        params,
      )}`,
    globalDatasources: `/perses/api/v1/globaldatasources`,
    search: `/perses/api/v1/dashboards/search`,
    getDashboardsOwnerUsers: `/perses/api/v1/dashboards/users`,
    move: `/perses/api/v1/dashboards/move`,
    savedSearchUsages: (savedSearchId: string) =>
      `/perses/api/v1/saved-searches/${encodeURIComponent(savedSearchId)}/dashboards`,
    snapshot: (uid: string, folderId: string) =>
      `/perses/api/v1/projects/${encodeURIComponent(folderId)}/dashboards/${encodeURIComponent(uid)}/snapshot`,
  };

  getAll = async (): Promise<UnifiedDashboardModel[]> => {
    return this.crudApiService.getAll(this.urls.project);
  };

  getOne(name: string, folderId: string, source?: 'grafana'): Promise<UnifiedDashboardModel> {
    return this.crudApiService.get(this.urls.instance(name, folderId, source ? { source } : undefined));
  }

  getById(dashboardId: string): Promise<UnifiedDashboardModel> {
    return this.crudApiService.get(this.urls.byId(dashboardId));
  }

  createDashboard = async (folderId: string, model: Partial<UnifiedDashboardModel>): Promise<UnifiedDashboardModel> => {
    return this.crudApiService.create(this.urls.resource(folderId), model as UnifiedDashboardModel);
  };

  update = async (model: Pick<UnifiedDashboardModel, 'uid' | 'doc'>, folderId): Promise<UnifiedDashboardModel> => {
    return this.crudApiService.update(this.urls.instance(model.uid, folderId), {
      doc: model.doc,
    } as UnifiedDashboardModel);
  };

  getGlobalDatasources = async () => {
    return this.datasourceApiService.getAll(this.urls.globalDatasources);
  };

  deleteDashboard = async (dashboardId: string, folderId: string) => {
    return this.crudApiService.delete(this.urls.instance(dashboardId, folderId));
  };

  search = async (requestObject: ISearchRequestObject) => {
    return this.crudApiService.search(this.urls.search, requestObject);
  };

  move = async (dashboardId: string, oldFolderId: string, newFolderId: string) => {
    return this.crudApiService.update(this.urls.move, {
      dashboardId,
      oldProjectId: oldFolderId,
      newProjectId: newFolderId,
    } as any);
  };

  getDashboardsOwnerUsers = async () => {
    return this.crudApiService.get<{ id: number; fullName: string }[]>(this.urls.getDashboardsOwnerUsers);
  };

  getSavedSearchUsages = async (savedSearchId: string): Promise<SavedSearchDashboardUsage[]> => {
    return this.savedSearchUsagesApiService.get<SavedSearchDashboardUsage[]>(this.urls.savedSearchUsages(savedSearchId));
  };

  getDashboardSnapshot = async (dashboardId: string, folderId: string) => {
    return this.crudApiService.do(this.urls.snapshot(dashboardId, folderId));
  };

  setPrivacy = async (folderId: string, dashboardId: string, isPrivate: boolean): Promise<{ isPrivate: boolean }> => {
    return this.crudApiService.update(
      `/perses/api/v1/projects/${encodeURIComponent(folderId)}/dashboards/${encodeURIComponent(dashboardId)}/privacy`,
      { isPrivate } as any,
    ) as unknown as { isPrivate: boolean };
  };
}

export const dashboardsApiService = new DashboardApiService();
